import type {
  ContactOrder,
  ContactOrderCreatePayload,
} from "../../../../../../../../../../../../../api/kehrblatt/contact-orders"
import { objectValue, stringValue } from "./sourceRows"
import type { InboxContactOrderSourceRow } from "./sourceRows"
import { parseCustomerInformationStepOutput, projectPersistedCustomerInformation } from "./outputs"

export type InboxContactOrderPayload = ContactOrderCreatePayload & {
  action_case_id?: string | null
  case_id?: string | null
}

export const inboxStepPayload = (
  payload: InboxContactOrderPayload,
  source: InboxContactOrderSourceRow,
): Record<string, unknown> => ({
  work_order_id: payload.work_order_id,
  schedule_event_id: payload.schedule_event_id ?? source.planned.schedule_event_id ?? null,
  source_schedule_event_id:
    source.planned.source_schedule_event_id ?? payload.previous_schedule_event_id ?? null,
  canceled_schedule_event_id:
    source.planned.canceled_schedule_event_id ?? payload.canceled_schedule_event_id ?? null,
  requester_customer_id: source.requesterCustomerId,
  ...(payload.planning_operation_id
    ? { planning_operation_id: payload.planning_operation_id }
    : {}),
  ...(payload.communication_selection
    ? { communication_selection: payload.communication_selection }
    : {}),
  assigned_employee_ids: payload.assigned_employee_ids ?? [],
  proposed_start_at: payload.proposed_start_at ?? source.event.start_at,
  proposed_end_at: payload.proposed_end_at ?? source.event.end_at,
  previous_schedule_event_id:
    payload.previous_schedule_event_id ??
    source.planned.canceled_schedule_event_id ??
    source.planned.source_schedule_event_id ??
    null,
  previous_appointment_start_at: payload.previous_appointment_start_at ?? null,
  previous_appointment_end_at: payload.previous_appointment_end_at ?? null,
  previous_schedule_event_status: payload.previous_schedule_event_status ?? null,
  contact_sequence_template_id: payload.contact_sequence_template_id ?? null,
})

type CustomerInformationCommitRow = { output: Record<string, unknown> }

const outputFromResultRow = (row: CustomerInformationCommitRow) => {
  const output = objectValue(row.output)

  return objectValue(output.customer_information || output)
}

export const syntheticContactOrderFromSuccess = ({
  row,
  payload,
  source,
  organizationId,
}: {
  row: CustomerInformationCommitRow
  payload: InboxContactOrderPayload
  source: InboxContactOrderSourceRow
  organizationId: string
}): ContactOrder | null => {
  const output = outputFromResultRow(row)
  const outcome = stringValue(output.outcome)
  const decision = stringValue(output.decision)

  if (outcome === "confirmation" || decision.startsWith("CONFIRMATION")) {
    return null
  }

  const contactOrderId =
    stringValue(output.contact_order_id) || `inbox-contact-order:${source.actionCaseId}`

  return {
    id: contactOrderId,
    organization_id: organizationId,
    case_id: null,
    work_order_id: payload.work_order_id,
    reason: source.contactOrderReason,
    status: "ACTIVE",
    schedule_event_id: payload.schedule_event_id ?? source.event.id,
    proposed_start_at: payload.proposed_start_at ?? source.event.start_at,
    proposed_end_at: payload.proposed_end_at ?? source.event.end_at,
    assigned_employee_ids: payload.assigned_employee_ids ?? [],
    // A commit receipt does not contain the persisted step settings; reload them normally.
    product_refs: {
      work_order_id: payload.work_order_id,
      schedule_event_id: payload.schedule_event_id ?? source.event.id,
      source_action_case_id: source.actionCaseId,
      communication_selection: payload.communication_selection,
    },
  } as ContactOrder
}

export const projectCustomerInformationSuccess = ({
  row,
  payload,
  source,
  event,
  organizationId,
}: {
  row: CustomerInformationCommitRow
  payload: InboxContactOrderPayload
  source: InboxContactOrderSourceRow
  event: ScheduleEvent
  organizationId: string
}): { event: ScheduleEvent; contactOrder: ContactOrder | null } => {
  const output = parseCustomerInformationStepOutput(row.output)

  if (output) {
    return projectPersistedCustomerInformation({
      output,
      actionCaseId: source.actionCaseId,
      organizationId,
      workOrder: source.workOrder,
      event,
      contactOrderReason: source.contactOrderReason,
    })
  }

  return {
    event,
    contactOrder: syntheticContactOrderFromSuccess({ row, payload, source, organizationId }),
  }
}
